import React, { useState } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import {
  LayoutDashboard, ClipboardList, Stethoscope, GraduationCap, Target, BookOpen,
  Coins, GitCompare, Trophy, Users, Settings as SettingsIcon, Bell, Sparkles, LogOut, Activity, Menu, MessageSquareHeart
} from "lucide-react";
import AIAssistant from "@/components/AIAssistant";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["all"] },
  { to: "/goals", label: "Goals & Objectives", icon: Target, roles: ["all"] },
  { to: "/daily-tracker", label: "Daily Tracker", icon: ClipboardList, roles: ["all"] },
  { to: "/surgery-log", label: "Surgery Log", icon: Stethoscope, roles: ["all"] },
  { to: "/sop-library", label: "OT SOP Library", icon: BookOpen, roles: ["all"] },
  { to: "/training", label: "Training & Growth", icon: GraduationCap, roles: ["all"] },
  { to: "/scorecard", label: "Scorecard & Ranking", icon: Trophy, roles: ["all"] },
  { to: "/owner-feedback", label: "Owner Feedback", icon: MessageSquareHeart, roles: ["super_admin", "ot_head"] },
  { to: "/incentives", label: "Incentives", icon: Coins, roles: ["super_admin", "ot_head"] },
  { to: "/branch-comparison", label: "Branch Comparison", icon: GitCompare, roles: ["super_admin", "ot_head"] },
  { to: "/master-list", label: "Master List", icon: Users, roles: ["super_admin", "ot_head"] },
  { to: "/settings", label: "Settings", icon: SettingsIcon, roles: ["super_admin"] },
];

function Filters() {
  const { filters, setFilter, branches, technicians, user } = useApp();
  const canBranch = ["super_admin", "ot_head"].includes(user?.app_role);
  const canTech = ["super_admin", "ot_head", "branch_coordinator"].includes(user?.app_role);
  const inp = "bg-[#0f172a] border border-white/10 rounded-md px-3 py-1.5 text-sm text-slate-200 focus:ring-2 focus:ring-[#C9A24B] focus:outline-none";
  return (
    <div className="flex flex-wrap items-center gap-2">
      {canBranch && (
        <select data-testid="branch-selector" className={inp} value={filters.branch} onChange={(e) => setFilter("branch", e.target.value)}>
          <option value="all">All Branches</option>
          {branches.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
      )}
      {canTech && (
        <select data-testid="technician-selector" className={inp} value={filters.technicianId} onChange={(e) => setFilter("technicianId", e.target.value)}>
          <option value="all">All Technicians</option>
          {technicians.filter(t => filters.branch === "all" || t.branch === filters.branch).map((t) => (
            <option key={t.employee_id} value={t.employee_id}>{t.name}</option>
          ))}
        </select>
      )}
      <input data-testid="date-start" type="date" className={inp} value={filters.start} onChange={(e) => setFilter("start", e.target.value)} />
      <span className="text-slate-500 text-xs">to</span>
      <input data-testid="date-end" type="date" className={inp} value={filters.end} onChange={(e) => setFilter("end", e.target.value)} />
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useApp();
  const [aiOpen, setAiOpen] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const role = user?.app_role;
  const items = NAV.filter((n) => n.roles.includes("all") || n.roles.includes(role));

  return (
    <div className="h-screen flex overflow-hidden bg-[#0b1120] text-slate-100">
      {/* Sidebar */}
      <aside className={`${mobileNav ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:static z-40 w-64 h-full bg-[#0F172A] border-r border-white/10 flex flex-col transition-transform`}>
        <div className="h-16 flex items-center gap-3 px-5 border-b border-white/10">
          <div className="w-9 h-9 rounded-lg bg-[#C9A24B] flex items-center justify-center text-[#0b1120]"><Activity size={20} /></div>
          <div>
            <div className="font-heading text-sm leading-tight">Youngness OT CRM</div>
            <div className="text-[10px] text-slate-500">by Dr. Awish</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {items.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} onClick={() => setMobileNav(false)}
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-200 border-l-2 ${
                  isActive ? "bg-white/5 text-[#C9A24B] border-[#C9A24B]" : "text-slate-400 border-transparent hover:text-white hover:bg-white/5"
                }`}>
              <n.icon size={18} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <button data-testid="logout-btn" onClick={logout} className="flex items-center gap-3 px-3 py-2.5 w-full rounded-md text-sm text-slate-400 hover:text-[#DC2626] hover:bg-white/5 transition-colors">
            <LogOut size={18} /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-auto min-h-16 py-3 flex flex-wrap items-center justify-between gap-3 px-4 md:px-6 bg-[#0F172A]/80 backdrop-blur-xl border-b border-white/10 z-30">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-slate-300" onClick={() => setMobileNav(!mobileNav)}><Menu /></button>
            <Filters />
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="ai-assistant-btn" onClick={() => setAiOpen(true)}
              className="flex items-center gap-2 bg-[#C9A24B] text-[#0b1120] font-semibold text-sm px-3 py-2 rounded-md hover:brightness-110 transition-all">
              <Sparkles size={16} /> AI Assistant
            </button>
            <button data-testid="notification-bell" className="relative text-slate-400 hover:text-white transition-colors">
              <Bell size={20} />
            </button>
            <div className="flex items-center gap-2 pl-2 border-l border-white/10">
              {user?.picture ? <img src={user.picture} alt="u" className="w-8 h-8 rounded-full" /> :
                <div className="w-8 h-8 rounded-full bg-[#C9A24B] text-[#0b1120] flex items-center justify-center text-sm font-bold">{user?.name?.[0]}</div>}
              <div className="hidden sm:block">
                <div className="text-sm leading-tight">{user?.name}</div>
                <div className="text-[10px] text-[#C9A24B] uppercase tracking-wide">{role?.replace("_", " ")}</div>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 md:p-8">
          <Outlet />
        </main>
      </div>

      <AIAssistant open={aiOpen} onClose={() => setAiOpen(false)} />
    </div>
  );
}
